import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { displayWorkOrderNumber } from "../components/snowflakes/work-orders/presentation";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { AssetId, WorkOrderId } from "../lib/workIds";
import { procedureExecutionQueryKey } from "./useProcedureExecution";

/**
 * Error returned when a Work Order update fails. `code` is optional because
 * non-conflict responses may not include one; consumers must refresh the Work
 * Order before retrying when `code` is `WORK_ORDER_STALE`.
 */
export class WorkOrderUpdateError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = "WorkOrderUpdateError";
  }
}

type WorkOrderUpdateFields = {
  // Omitted when the Work Order is read-only (Done or Canceled), where the
  // API refuses a change to any of the three.
  title?: string;
  /** `null` removes the Asset. */
  assetId?: AssetId | null;
  /** The full set of Categories the Work Order should carry. */
  categoryIds?: number[];
  description: string | null;
  instructions: string | null;
  priorityCode: string | null;
  statusCode: string;
  workTypeCode: string;
  plannerGroupCode: string | null;
  // Named to match the backend's updateWorkOrderRequestSchema field
  // (Work.WorkOrder's DateBasicStart column), while the edit form itself
  // uses the shorter `startDate` for its draft state.
  basicStartDate: string | null;
  dueDate: string | null;
};

export type UpdateWorkOrderInput =
  | {
      workOrderId: WorkOrderId;
      workOrderNumber: string;
      version: string;
      statusCode: string;
    }
  | (WorkOrderUpdateFields & {
      workOrderId: WorkOrderId;
      workOrderNumber: string;
      version: string;
    });

export function useWorkOrderUpdateMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workOrderId,
      // Display-only; the API does not accept it.
      workOrderNumber: _workOrderNumber,
      ...input
    }: UpdateWorkOrderInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].$patch({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId,
        },
        json: input,
      });
      if (!response.ok) {
        const errorBody = (await response.json().catch(() => null)) as {
          error?: string;
          code?: string;
        } | null;
        throw new WorkOrderUpdateError(
          errorBody?.error ??
            (response.status === 403
              ? "You do not have permission to edit this Work Order."
              : `Unable to save Work Order details (${response.status}).`),
          errorBody?.code,
        );
      }
      return response.json();
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Order ${displayWorkOrderNumber(variables.workOrderNumber)} updated.`,
        { icon: true },
      );
      await Promise.all([
        // A status change (such as reopening) changes whether the Procedure
        // is editable, so its cached execution must be reloaded.
        queryClient.invalidateQueries({
          queryKey: procedureExecutionQueryKey(
            selectedCustomer?.id,
            selectedFacility?.id,
            variables.workOrderId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: [
            "work-order-detail",
            selectedCustomer?.id,
            selectedFacility?.id,
            variables.workOrderId,
          ],
        }),
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
        // Moving a canceled Work Order back to an active status restores its
        // linked Request on the server, so cached Requests are now stale.
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to save Work Order details. Try again.", {
        icon: true,
      });
    },
  });
}
